import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { AlertProvider } from './contexts/AlertContext';
import { PrivateRoute } from './components/PrivateRoute';
import { AdminRoute } from './components/AdminRoute';
import { captureAttributionFromCurrentUrl } from './services/analyticsService';
import { captureAdClickIdsFromCurrentUrl } from './services/adClickIds';
import AnalyticsConsentBanner, { CookieChoicesLink } from './components/AnalyticsConsentBanner';
import { theme } from './styles';

// Pages
import Welcome from './pages/Welcome';
import Account from './pages/Account';
import Budgets from './pages/Budgets';
import Activity from './pages/Activity';
import Money from './pages/Money';
import PlaidLinkRedirect from './pages/PlaidLinkRedirect';
import Bills from './pages/Bills';
import Goals from './pages/Goals';
import SubscriptionCancel from './pages/SubscriptionCancel';
import Support from './pages/Support';
import Discover from './pages/Discover';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ContestRules from './pages/ContestRules';
import FeedbackRewardTerms from './pages/FeedbackRewardTerms';
import PricingModal from './components/PricingModal';
import Referral from './pages/Referral';
import RitualReset from './pages/RitualReset';
import DesignSystem from './pages/DesignSystem';
import Download from './pages/Download';
import Pricing from './pages/Pricing';
import EmailPreferences from './pages/EmailPreferences';
import NotFound from './pages/NotFound';
import AuthEntryRedirect from './components/AuthEntryRedirect';

// Admin pages - lazy loaded
import {
  AdminLogin,
  AdminDashboard,
  AdminUsers,
  AdminDiscover,
  AdminContest,
  AdminAlerts,
  AdminSocial,
  AdminFeatureAdoption,
  AdminCustomerCommunications,
  AdminCampaigns
} from './pages/admin';
import { isAdminHost } from './utils/host';

// Captures UTM/ref query params on every route mount/change so attribution
// works regardless of which public page the user lands on (#721, #722).
// First-touch wins, so re-entering /login after landing on / is safe.
// Ad click ids (#2499) are stored only after the analytics consent grant.
const AttributionCapture = () => {
  const location = useLocation();
  useEffect(() => {
    captureAttributionFromCurrentUrl({ captureMethod: 'app_init' });
    captureAdClickIdsFromCurrentUrl();
  }, [location.pathname, location.search]);
  return null;
};

// Loading fallback for lazy-loaded admin pages
const AdminLoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

// Admin routes component - rendered when on admin subdomain
const AdminRoutes = () => (
  <Suspense fallback={<AdminLoadingFallback />}>
    <Routes>
      {/* Login page - no auth required */}
      <Route path="/login" element={<AdminLogin />} />
      {/* Admin portal requires authentication + admin access */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      {/* Keep /metrics as alias/redirect for backward compatibility */}
      <Route path="/metrics" element={<Navigate to="/dashboard" replace />} />
      <Route path="/users" element={
        <AdminRoute>
          <AdminUsers />
        </AdminRoute>
      } />
      <Route path="/discover" element={
        <AdminRoute>
          <AdminDiscover />
        </AdminRoute>
      } />
      <Route path="/contest" element={
        <AdminRoute>
          <AdminContest />
        </AdminRoute>
      } />
      <Route path="/alerts" element={
        <AdminRoute>
          <AdminAlerts />
        </AdminRoute>
      } />
      <Route path="/campaigns/:campaignId?" element={
        <AdminRoute>
          <AdminCampaigns />
        </AdminRoute>
      } />
      <Route path="/social" element={
        <AdminRoute>
          <AdminSocial />
        </AdminRoute>
      } />
      <Route path="/feature-adoption" element={
        <AdminRoute>
          <AdminFeatureAdoption />
        </AdminRoute>
      } />
      <Route path="/communications" element={
        <AdminRoute>
          <AdminCustomerCommunications />
        </AdminRoute>
      } />
      {/* Catch-all redirect to dashboard (AdminRoute will redirect to login if not authenticated) */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </Suspense>
);

// Main app routes - rendered on main domain.
// Exported for tests that must assert the real route table (#1390 S1).
export const MainRoutes = ({ showPricing, setShowPricing }) => (
  <>
    <Routes>
      <Route path="/" element={<Welcome />} />
      {/* #1390: conventional auth paths → landing + open Login modal */}
      <Route path="/login" element={<AuthEntryRedirect />} />
      <Route path="/signup" element={<AuthEntryRedirect />} />
      <Route path="/register" element={<AuthEntryRedirect />} />
      <Route path="/r/:code" element={<Referral />} />
      {/* Sunday Reset token invite (#1212) — printed export band + deep link */}
      <Route path="/reset/:code" element={<RitualReset />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contest-rules" element={<ContestRules />} />
      <Route path="/feedback-reward-terms" element={<FeedbackRewardTerms />} />
      <Route path="/download" element={<Download />} />
      <Route path="/pricing" element={<Pricing />} />
      {/* #1213: /compare/* retired — host 301 in backend/app.js; client catch-all if SPA loads first */}
      <Route path="/compare" element={<Navigate to="/" replace />} />
      <Route path="/compare/*" element={<Navigate to="/" replace />} />
      <Route path="/design-system" element={<DesignSystem />} />
      {/* No-login email preference center; token from the marketing email footer */}
      <Route path="/email-preferences" element={<EmailPreferences />} />
      <Route path="/dashboard" element={<Navigate to="/activity" replace />} />
      <Route path="/activity" element={
        <PrivateRoute>
          <Activity />
        </PrivateRoute>
      } />
      <Route path="/account" element={
        <PrivateRoute>
          <Account />
        </PrivateRoute>
      } />
      <Route path="/budgets" element={
        <PrivateRoute>
          <Budgets />
        </PrivateRoute>
      } />
      <Route path="/plaid-link" element={
        <PrivateRoute>
          <PlaidLinkRedirect />
        </PrivateRoute>
      } />
      <Route path="/money" element={
        <PrivateRoute>
          <Money />
        </PrivateRoute>
      } />
      <Route path="/bills" element={
        <PrivateRoute>
          <Bills />
        </PrivateRoute>
      } />
      <Route path="/goals" element={
        <PrivateRoute>
          <Goals />
        </PrivateRoute>
      } />
      <Route path="/subscription/cancel" element={
        <PrivateRoute>
          <SubscriptionCancel />
        </PrivateRoute>
      } />
      <Route path="/support" element={
        <PrivateRoute>
          <Support />
        </PrivateRoute>
      } />
      <Route path="/discover" element={
        <PrivateRoute>
          <Discover />
        </PrivateRoute>
      } />
      {/* #1390: unmatched paths show a real not-found UI (was blank cream root) */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    <PricingModal 
      open={showPricing} 
      onClose={() => {
        try {
          // Suppress auto-open for 5 minutes
          sessionStorage.setItem('upgradeModalSuppressUntil', String(Date.now() + 5 * 60 * 1000));
        } catch (_) {}
        setShowPricing(false);
      }} 
    />
  </>
);

const App = () => {
  const [showPricing, setShowPricing] = useState(false);
  const isAdmin = isAdminHost();

  useEffect(() => {
    const handler = (e) => {
      try {
        const force = e?.detail?.force === true;
        if (!force) {
          const suppressUntil = Number(sessionStorage.getItem('upgradeModalSuppressUntil') || '0');
          if (Date.now() < suppressUntil) return;
        }
      } catch (_) {}
      setShowPricing(true);
    };
    window.addEventListener('subscription:upgrade', handler);
    return () => window.removeEventListener('subscription:upgrade', handler);
  }, []);

  // Close PricingModal when returning from Stripe checkout
  useEffect(() => {
    const handler = () => {
      setShowPricing(false);
    };
    window.addEventListener('subscription:close-pricing-modal', handler);
    return () => window.removeEventListener('subscription:close-pricing-modal', handler);
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <ErrorProvider>
          <Router>
            <AttributionCapture />
            <AuthProvider>
              <AlertProvider>
                {isAdmin ? (
                  <AdminRoutes />
                ) : (
                  <>
                    <MainRoutes showPricing={showPricing} setShowPricing={setShowPricing} />
                    {/* #1216 — analytics and advertising choice; not shown on admin host */}
                    <AnalyticsConsentBanner />
                    <CookieChoicesLink />
                  </>
                )}
              </AlertProvider>
            </AuthProvider>
          </Router>
        </ErrorProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
